import { HttpClient, provideHttpClient, withInterceptorsFromDi, withJsonpSupport } from '@angular/common/http';
import {
    ApplicationConfig,
    ErrorHandler,
    inject,
    provideAppInitializer,
    provideZoneChangeDetection
} from '@angular/core';
import { provideRouter, UrlSerializer, withRouterConfig } from '@angular/router';
import { provideMissingTranslationHandler, provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { provideSfxCommons } from '@swissfex/sfx-commons';
import { provideOAuthClient } from 'angular-oauth2-oidc';
import { provideNzConfig } from 'ng-zorro-antd/core/config';
import { provideClarity } from 'ngx-clarity';
import { provideMarkdown } from 'ngx-markdown';

import { environment } from '../environments/environment';
import { AppInitializerService } from './core/services/app-initializer.service';
import { SfxErrorHandler } from './core/sfx-error-handler/sfx-error-handler';
import { CleanUrlSerializer } from './core/utils/clean-url-serializer';
import { provideSfxDiInterceptors } from './core/utils/http-interceptors.utils';
import { MadMissingTranslationHandler } from './mad-translate/mad-missing-translations-handler';
import { HttpTranslateLoaderFactory } from './mad-translate/mad-translate-loader';
import { MadTranslateService } from './mad-translate/services/mad-translate.service';
import { AuthenticationService } from './projects/services/authentication.service';

const appInitializerFn = (
    appInitSvc: AppInitializerService,
    authSvc: AuthenticationService,
    madTranslateSvc: MadTranslateService
) => {
    return async () => {
        try {
            await appInitSvc.init();
            await madTranslateSvc.init();
            await authSvc.init();
        } catch (ex) {
            console.error(ex);
        }
    };
};

export const appConfig: ApplicationConfig = {
    providers: [
        provideHttpClient(withJsonpSupport(), withInterceptorsFromDi()),
        provideSfxDiInterceptors(),
        provideOAuthClient(),
        provideRouter([], withRouterConfig({ paramsInheritanceStrategy: 'always' })),
        provideTranslateService({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpTranslateLoaderFactory,
                deps: [HttpClient]
            },
            missingTranslationHandler: provideMissingTranslationHandler(() => new MadMissingTranslationHandler())
        }),
        AppInitializerService,
        provideAppInitializer(() =>
            appInitializerFn(
                inject(AppInitializerService),
                inject(AuthenticationService),
                inject(MadTranslateService)
            )()
        ),
        { provide: ErrorHandler, useClass: SfxErrorHandler },
        { provide: UrlSerializer, useClass: CleanUrlSerializer },
        provideNzConfig({ notification: { nzPlacement: 'bottomRight' } }),
        provideSfxCommons(),
        provideMarkdown(),
        provideClarity({
            enabled: environment.features.clarity.isEnabled,
            projectId: environment.features.clarity.key
        }),
        provideZoneChangeDetection()
    ]
};
